import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Icon } from "../components/Icon";
import { DEMO_MODE } from "../lib/config";
import { api } from "../lib/api";
import { fmtCurrency } from "../lib/format";
import { DEMO_SESSION, DEMO_PATIENT } from "../lib/demoData";
import type { Session, Patient } from "../types";

interface SessionRow extends Session {
  patient?: Patient;
}

export function SessionsListPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["sessions"],
    queryFn: async (): Promise<SessionRow[]> => {
      if (DEMO_MODE) {
        return [{ ...DEMO_SESSION, patient: DEMO_PATIENT }];
      }
      return await api.get<SessionRow[]>("/sessions");
    },
  });

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Active sessions</h1>
          <p className="page__sub">Open sessions for patient financing applications.</p>
        </div>
        <div className="page__actions">
          <Link to="/sessions/new" className="btn btn--solid">
            <Icon.Plus width={14} height={14} /> New session
          </Link>
        </div>
      </div>

      {isLoading && <div className="empty">Loading…</div>}
      {!isLoading && (!data || data.length === 0) && (
        <div className="empty">
          No active sessions. <Link to="/sessions/new">Start a new one</Link>.
        </div>
      )}
      {!isLoading && data && data.length > 0 && (
        <div className="card">
          {data.map((s) => (
            <Link key={s.id} to={`/sessions/${s.id}`} className="card__row">
              <div className="card__row-meta">
                <span className="card__row-title">
                  {s.patient
                    ? `${s.patient.firstName} ${s.patient.lastName}`
                    : `Patient ${s.patientId}`}
                </span>
                <span className="card__row-sub">
                  {s.treatmentType} · {fmtCurrency(s.requestedAmount)} · started{" "}
                  {new Date(s.startedAt).toLocaleString("en-US", {
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </span>
              </div>
              <Icon.Arrow width={14} height={14} />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
